import {
	inject,
	Injectable,
	Injector,
	runInInjectionContext,
} from '@angular/core';
import {
	PluginType,
	CrosswordPuzzleGameData,
	CrosswordCell,
	PlacedWord,
	WordIntersection,
	CrosswordPuzzleGameService,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
	GenerationStep,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Optimization sort mode - determines how optimization results are ranked
 */
export type OptimizationSortMode = 'fillRatio' | 'gridSize';

/**
 * Optimization intensity levels - determines how thoroughly to test combinations
 */
export type OptimizationIntensity =
	| 'quick'
	| 'normal'
	| 'thorough'
	| 'exhaustive';

/**
 * Settings for crossword optimization algorithm
 */
export interface OptimizationSettings {
	// How intensively to test combinations
	intensity: OptimizationIntensity;
	// Maximum allowed grid dimensions
	maxGridRows: number;
	maxGridCols: number;
	// How many optimization results to return
	optimizationResultCount: number;
	// How to sort the results
	sortMode: OptimizationSortMode;
	// Whether to test all combinations of main words (2^n - 1 combinations)
	testAllMainWordCombinations?: boolean;
	// Whether to prioritize shorter words in secondary word selection
	prioritizeShorterWords?: boolean;
}

/**
 * Result from testing one word combination
 */
export interface OptimizationResult {
	// The tested word subset
	secondaryWords: string[];
	// Grid dimensions after trimming
	gridRows: number;
	gridCols: number;
	// Grid metrics
	totalCells: number;
	filledCells: number;
	fillRatio: number;
	// Placement metrics
	placedCount: number;
	totalIntersections: number;
	mainWordIntersections: number;
	// Pre-generated grid data (avoids regeneration)
	grid?: CrosswordCell[][];
	placedWords?: PlacedWord[];
	intersections?: WordIntersection[];
}

/**
 * Crossword puzzle specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class CrosswordPuzzleGenerationService extends BaseGenerationService<CrosswordPuzzleGameData> {
	readonly pluginType = PluginType.CrosswordPuzzle;

	// Inject Injector for creating game service instances with proper injection context
	private injector = inject(Injector);

	/**
	 * Create system and user prompts for crossword puzzle generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;
		const generationType = parameters['generationType'] || 'mainSentence';

		// Get target language prompt from settings service
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		// Get target difficulty prompt from settings service
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		const existingMainWords = parameters['existingMainWords'] || [];
		const existingSecondaryWords =
			parameters['existingSecondaryWords'] || [];

		let systemPrompt: string;
		let userPrompt: string;

		if (generationType === 'mainSentence') {
			// Main words generation with individual clues
			systemPrompt = `Olet kokenut ristisanatehtävien laatija ja tiedät, mikä lukijoita kiinnostaa.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

			const existingMainWords = parameters['existingMainWords'] || [];
			const existingWordsSection =
				existingMainWords.length > 0
					? `\n\nLisäksi sinun tulee luoda vihjeet seuraaville olemassa oleville pääsanoille:\n${existingMainWords.join(', ')}\n\nHUOM: Näitä olemassa olevia sanoja EI lasketa mukaan luotavien ${request.parameters['mainWordAmount'] || 1} UUDEN pääsanan määrään. Luot siis yhteensä ${request.parameters['mainWordAmount'] || 1} uutta sanaa SEKÄ vihjeet ${existingMainWords.length} olemassa olevalle sanalle.`
					: '';

			userPrompt = `
		Tee tiedostohakuja lähdemateriaaliin hakutyökalulla ja generoi ${request.parameters['mainWordAmount'] || 1} UUTTA pääsanaa ristikkoa varten. Sanat tulisi liittyä vahvasti lähdemateriaali pääteemaan. Keskity sanoihin, jotka soveltuvat ristikon pääsanoiksi ja kuvastavat lähdemateriaalin teemaa.
		${existingWordsSection}

		Generoi jokaiselle sanalle (sekä uudet että olemassa olevat) siihen liittyvä vihje, joka on lyhyt, ytimekäs ja selkeä. Vihjeen tulisi auttaa pelaajaa arvaamaan sana, mutta ei paljastaa sitä suoraan. Tunnista, millaisia ristikkosanojen vihjeet yleensä ovat ja kirjoita vihje samalla tyylillä. Vihje ei missään tapauksessa saa sisältää itse pääsanaa.

		Palauta JSON-objekti, jossa on seuraava kenttä:
		- "mainWords" // (objekti[]), jonka sisällä on seuraavat kentät:
		- * "word" // (string) Pääsana ristikkoa varten (sekä uudet että olemassa olevat sanat)
		- * "clue" // (string) Vihje sanalle, joka auttaa pelaajaa arvaamaan sen

${targetLanguagePrompt}
${targetDifficultyPrompt}`;
		} else if (generationType === 'secondaryWords') {
			// Secondary words generation - extracted from original GenerateSecondaryWords method
			systemPrompt = `Olet kokenut ristikoiden laatija. Tehtäväsi on generoida sanoja, jotka voivat risteä annettujen pääsanojen kanssa ristikossa. Palauta aina validoitu JSON-objekti pyydetyssä muodossa.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

			const mainWords = parameters['mainWords'] || [];
			const existingSecondaryWords =
				parameters['existingSecondaryWords'] || [];

			const existingSecondaryWordsSection =
				existingSecondaryWords.length > 0
					? `\n\nLisäksi sinun tulee luoda vihjeet seuraaville olemassa oleville risteäville sanoille:\n${existingSecondaryWords.join(', ')}\n\nHUOM: Näitä olemassa olevia sanoja EI lasketa mukaan luotavien $${request.parameters['secondaryWordAmount'] !== undefined ? request.parameters['secondaryWordAmount'] : this.getDefaultParameters().secondaryWordAmount} UUDEN risteävän sanan määrään. Luot siis yhteensä $${request.parameters['secondaryWordAmount'] !== undefined ? request.parameters['secondaryWordAmount'] : this.getDefaultParameters().secondaryWordAmount} uutta sanaa SEKÄ vihjeet ${existingSecondaryWords.length} olemassa olevalle sanalle.`
					: '';

			userPrompt = `
		Pääsanat: ${mainWords.join(', ')}
		
		Generoi ${request.parameters['secondaryWordAmount'] !== undefined ? request.parameters['secondaryWordAmount'] : this.getDefaultParameters().secondaryWordAmount} UUTTA sanaa, jotka voivat risteä näiden pääsanojen kanssa. Sanat voivat olla substantiiveja, adjektiiveja tai verbejä. Ota materiaalista lyhyitä tai keskipitkiä sanoja. Vältä pitkiä sanoja ja yhdyssanoja. Ota sanojen aiheet lähdemateriaalista, jos mahdollista. On tärkeää, että ne liittyvät pääsanojen aiheeseen, mutta esiintyvät myös materiaalissa.
	${existingSecondaryWordsSection}
		
		Generoi jokaiselle sanalle (sekä uudet että olemassa olevat) siihen liittyvä vihje, joka on lyhyt, ytimekäs ja selkeä. Vihjeen tulisi auttaa pelaajaa arvaamaan sana, mutta ei paljastaa sitä suoraan. Vihje voi olla esimerkiksi sana, lause tai lyhyt kuvaus. Tunnista, millaisia ristikkosanojen vihjeet yleensä ovat ja kirjoita vihje samalla tyylillä. Vihje ei missään tapauksessa saa sisältää itse pääsanaa.
		
		Palauta JSON-objekti, jossa on seuraava kenttä:
		- "secondaryWords" // (objekti[]), jonka sisällä on seuraavat kentät:
		- * "word" // (string) Sana, joka voi risteää pääsanojen kanssa (sekä uudet että olemassa olevat sanat)
		- * "clue" // (string) Vihje sanalle, joka auttaa pelaajaa arvaamaan sen

${targetLanguagePrompt}
${targetDifficultyPrompt}`;
		} else if (generationType === 'mainAndSecondaryWords') {
			// Combined generation for both main and secondary words
			systemPrompt = `Olet kokenut ristisanatehtävien laatija ja tiedät, mikä lukijoita kiinnostaa. Tehtäväsi on luoda kokonainen ristikko sekä pää- että risteävillä sanoilla. Palauta aina validoitu JSON-objekti pyydetyssä muodossa.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

			userPrompt = `
		Tee tiedostohakuja lähdemateriaaliin hakutyökalulla ja luo kokonainen ristikko.

		Ensin generoi ${request.parameters['mainWordAmount'] !== undefined ? request.parameters['mainWordAmount'] : this.getDefaultParameters().mainWordAmount} UUTTA pääsanaa, jotka liittyvät vahvasti lähdemateriaali pääteemaan. Sanojen tulisi sopia ristikon pääsanoiksi. Vältä valtavan pitkiä sanoja tai yhdyssanoja.
	${existingMainWords.length > 0 ? `\n\nLisäksi sinun tulee luoda vihjeet seuraaville olemassa oleville pääsanoille:\n${existingMainWords.join(', ')}\n\nHUOM: Näitä olemassa olevia sanoja EI lasketa mukaan luotavien ${request.parameters['mainWordAmount'] !== undefined ? request.parameters['mainWordAmount'] : this.getDefaultParameters().mainWordAmount} UUDEN pääsanan määrään. Luot siis yhteensä ${request.parameters['mainWordAmount'] !== undefined ? request.parameters['mainWordAmount'] : this.getDefaultParameters().mainWordAmount} uutta sanaa SEKÄ vihjeet ${existingMainWords.length} olemassa olevalle sanalle.` : ''}

		Sitten generoi ${request.parameters['secondaryWordAmount'] !== undefined ? request.parameters['secondaryWordAmount'] : this.getDefaultParameters().secondaryWordAmount} UUTTA risteävää sanaa, jotka voivat risteä pääsanojen kanssa. Risteävät sanat voivat olla substantiiveja, adjektiiveja tai verbejä. Vältä liian pitkiä sanoja ja yhdyssanoja - pyri löytämään lyhyitä ja keskipitkiä sanoja materiaalista ja sen aiheesta.
	${existingSecondaryWords.length > 0 ? `\n\nLisäksi sinun tulee luoda vihjeet seuraaville olemassa oleville risteäville sanoille:\n${existingSecondaryWords.join(', ')}\n\nHUOM: Näitä olemassa olevia sanoja EI lasketa mukaan luotavien ${request.parameters['secondaryWordAmount'] || this.getDefaultParameters().secondaryWordAmount || 10} UUDEN risteävän sanan määrään. Luot siis yhteensä ${request.parameters['secondaryWordAmount'] || this.getDefaultParameters().secondaryWordAmount || 10} uutta sanaa SEKÄ vihjeet ${existingSecondaryWords.length} olemassa olevalle sanalle.` : ''}

		Generoi jokaiselle sanalle (sekä pää- että risteäville) siihen liittyvä vihje, joka on lyhyt, ytimekäs ja selkeä. Vihjeen tulisi auttaa pelaajaa arvaamaan sana, mutta ei paljastaa sitä suoraan. Tunnista, millaisia ristikkosanojen vihjeet yleensä ovat ja kirjoita vihje samalla tyylillä. Vihje ei missään tapauksessa saa sisältää itse pääsanaa.

		Palauta JSON-objekti, jossa on seuraavat kentät:
		- "mainWords" // (objekti[]), jonka sisällä on seuraavat kentät:
		- * "word" // (string) Pääsana ristikkoa varten (sekä uudet että olemassa olevat sanat)
		- * "clue" // (string) Vihje sanalle, joka auttaa pelaajaa arvaamaan sen
		- "secondaryWords" // (objekti[]), jonka sisällä on seuraavat kentät:
		- * "word" // (string) Sana, joka voi risteää pääsanojen kanssa (sekä uudet että olemassa olevat sanat)
		- * "clue" // (string) Vihje sanalle, joka auttaa pelaajaa arvaamaan sen
		- "backgroundImageId": -1 // jos mukana on kuvia, valitse kuvista jokin pääsanaan liittyvä kuva. Jos tällaista ei löydy, ota jokin yleisteemaan sopiva kuva ja laita sen id tähän.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;
		} else if (generationType === 'optimizedCrossword') {
			// Combined generation with optimization
			// Use same prompts as 'mainAndSecondaryWords' since optimization happens after word generation
			systemPrompt = `Olet kokenut ristisanatehtävien laatija ja tiedät, mikä lukijoita kiinnostaa. Tehtäväsi on luoda kokonainen ristikko sekä pää- että risteävillä sanoilla. Palauta aina validoitu JSON-objekti pyydetyssä muodossa.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

			userPrompt = `
		Tee tiedostohakuja lähdemateriaaliin hakutyökalulla ja luo kokonainen ristikko.

		Ensin generoi ${request.parameters['mainWordAmount'] !== undefined ? request.parameters['mainWordAmount'] : this.getDefaultParameters().mainWordAmount} UUTTA pääsanaa, jotka liittyvät vahvasti lähdemateriaali pääteemaan. Etsi sanoja, jotka sopivat ristikon pääsanoiksi. Vältä valtavan pitkiä sanoja tai yhdyssanoja.
	${existingMainWords.length > 0 ? `\n\nLisäksi sinun tulee luoda vihjeet seuraaville olemassa oleville pääsanoille:\n${existingMainWords.join(', ')}\n\nHUOM: Näitä olemassa olevia sanoja EI lasketa mukaan luotavien ${request.parameters['mainWordAmount'] !== undefined ? request.parameters['mainWordAmount'] : this.getDefaultParameters().mainWordAmount} UUDEN pääsanan määrään. Luot siis yhteensä ${request.parameters['mainWordAmount'] !== undefined ? request.parameters['mainWordAmount'] : this.getDefaultParameters().mainWordAmount} uutta sanaa SEKÄ vihjeet ${existingMainWords.length} olemassa olevalle sanalle.` : ''}

		Sitten generoi ${request.parameters['secondaryWordAmount'] !== undefined ? request.parameters['secondaryWordAmount'] : this.getDefaultParameters().secondaryWordAmount} UUTTA risteävää sanaa, jotka voivat risteä pääsanojen kanssa. Risteävät sanat voivat olla substantiiveja, adjektiiveja tai verbejä. Vältä liian pitkiä sanoja ja yhdyssanoja - pyri löytämään lyhyitä ja keskipitkiä sanoja materiaalista ja sen aiheesta.
	${existingSecondaryWords.length > 0 ? `\n\nLisäksi sinun tulee luoda vihjeet seuraaville olemassa oleville risteäville sanoille:\n${existingSecondaryWords.join(', ')}\n\nHUOM: Näitä olemassa olevia sanoja EI lasketa mukaan luotavien ${request.parameters['secondaryWordAmount'] || this.getDefaultParameters().secondaryWordAmount || 10} UUDEN risteävän sanan määrään. Luot siis yhteensä ${request.parameters['secondaryWordAmount'] || this.getDefaultParameters().secondaryWordAmount || 10} uutta sanaa SEKÄ vihjeet ${existingSecondaryWords.length} olemassa olevalle sanalle.` : ''}

		Generoi jokaiselle sanalle (sekä pää- että risteäville) siihen liittyvä vihje, joka on lyhyt, ytimekäs ja selkeä. Vihjeen tulisi auttaa pelaajaa arvaamaan sana, mutta ei paljastaa sitä suoraan. Tunnista, millaisia ristikkosanojen vihjeet yleensä ovat ja kirjoita vihje samalla tyylillä. Vihje ei missään tapauksessa saa sisältää itse pääsanaa.

		Palauta JSON-objekti, jossa on seuraavat kentät:
		- "title" // (string) Ristikon otsikko, joka kuvaa lähdemateriaalin pääteemaa (esim. "Suomen historia" tai "Luonnontiede")
		- "mainWords" // (objekti[]), jonka sisällä on seuraavat kentät:
		- * "word" // (string) Pääsana ristikkoa varten (sekä uudet että olemassa olevat sanat)
		- * "clue" // (string) Vihje sanalle, joka auttaa pelaajaa arvaamaan sen
		- "secondaryWords" // (objekti[]), jonka sisällä on seuraavat kentät:
		- * "word" // (string) Sana, joka voi risteää pääsanojen kanssa (sekä uudet että olemassa olevat sanat)
		- * "clue" // (string) Vihje sanalle, joka auttaa pelaajaa arvaamaan sen
		- "backgroundImageId": -1 // jos mukana on kuvia, valitse kuvista jokin pääsanaan liittyvä kuva. Jos tällaista ei löydy, ota jokin yleisteemaan sopiva kuva ja laita sen id tähän.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;
		} else {
			throw new Error(`Unsupported generation type: ${generationType}`);
		}

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Process AI response into CrosswordPuzzleGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	protected processResponseCore(
		response: any,
		parameters: any,
	): GenerationResult {
		try {
			const generationType =
				parameters['generationType'] || 'mainSentence';

			if (generationType === 'mainSentence') {
				return this.processMainWordsResponse(response, parameters);
			} else if (generationType === 'secondaryWords') {
				return this.processSecondaryWordsResponse(response, parameters);
			} else if (generationType === 'mainAndSecondaryWords') {
				return this.processMainAndSecondaryWordsResponse(
					response,
					parameters,
				);
			} else if (generationType === 'optimizedCrossword') {
				return this.processOptimizedCrosswordResponse(
					response,
					parameters,
				);
			} else {
				return {
					success: false,
					error: `Unsupported generation type: ${generationType}`,
					rawResponse: response.rawResponse,
				};
			}
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Process main words response (individual words with clues)
	 */
	private processMainWordsResponse(
		response: any,
		parameters: any,
	): GenerationResult {
		if (
			!response?.json?.mainWords ||
			!Array.isArray(response.json.mainWords)
		) {
			return {
				success: false,
				error: 'Invalid response format: no mainWords found',
				rawResponse: response.rawResponse,
			};
		}

		// Validate main words structure
		for (const wordObj of response.json.mainWords) {
			if (!wordObj.word || !wordObj.clue) {
				return {
					success: false,
					error: 'Invalid main word structure in response',
					rawResponse: response.rawResponse,
				};
			}
		}

		// Create game data with main words
		const gameData = new CrosswordPuzzleGameData();

		// Extract title from response (if provided)
		if (response.json.title) {
			gameData.title = response.json.title;
		}

		// Add main words and their clues
		// Always add ALL words from the response (both new and existing words with hints)
		// The merge logic will handle replacement/merging based on clearPrevious flag
		response.json.mainWords.forEach(
			(obj: { word: string; clue: string }) => {
				const word = obj.word.toUpperCase();
				// Add the word to mainWords array only if not already present
				if (!gameData.mainWords.includes(word)) {
					gameData.mainWords.push(word);
					// Add the clue
					gameData.mainWordClues[word] = obj.clue;
				} else {
					console.warn(
						`[Crossword Generation] Duplicate main word detected and skipped: "${word}"`,
					);
				}
			},
		);

		return {
			success: true,
			gameData,
			rawResponse: response.rawResponse,
		};
	}

	/**
	 * Process secondary words response
	 */
	private processSecondaryWordsResponse(
		response: any,
		parameters: any,
	): GenerationResult {
		const generatedSecondaryWords = response.json?.secondaryWords;

		if (
			!generatedSecondaryWords ||
			!Array.isArray(generatedSecondaryWords) ||
			generatedSecondaryWords.length === 0
		) {
			return {
				success: false,
				error: 'Invalid response format: no secondary words found',
				rawResponse: response.rawResponse,
			};
		}

		// Validate secondary words structure
		for (const wordObj of generatedSecondaryWords) {
			if (!wordObj.word || !wordObj.clue) {
				return {
					success: false,
					error: 'Invalid secondary word structure in response',
					rawResponse: response.rawResponse,
				};
			}
		}

		// Create game data
		const gameData = new CrosswordPuzzleGameData();

		// Add secondary words and their clues
		// Always add ALL words from the response (both new and existing words with hints)
		// The merge logic will handle replacement/merging based on clearPrevious flag
		generatedSecondaryWords.forEach(
			(obj: { word: string; clue: string }) => {
				const word = obj.word.toUpperCase();
				// Add the word to secondaryWords array only if not already present
				// Also check against mainWords to prevent same word appearing in both arrays
				if (
					!gameData.secondaryWords.includes(word) &&
					!gameData.mainWords.includes(word)
				) {
					gameData.secondaryWords.push(word);
					// Add the clue
					gameData.secondaryWordClues[word] = obj.clue;
				} else if (gameData.mainWords.includes(word)) {
					console.warn(
						`[Crossword Generation] Word "${word}" already exists as main word, skipping as secondary word`,
					);
				} else {
					console.warn(
						`[Crossword Generation] Duplicate secondary word detected and skipped: "${word}"`,
					);
				}
			},
		);

		gameData.backgroundImageId = response?.backgroundImageId || -1;

		return {
			success: true,
			gameData,
			rawResponse: response.rawResponse,
		};
	}

	/**
	 * Process combined main and secondary words response
	 */
	private processMainAndSecondaryWordsResponse(
		response: any,
		parameters: any,
	): GenerationResult {
		const generatedMainWords = response.json?.mainWords;
		const generatedSecondaryWords = response.json?.secondaryWords;

		// Validate main words
		if (
			!generatedMainWords ||
			!Array.isArray(generatedMainWords) ||
			generatedMainWords.length === 0
		) {
			return {
				success: false,
				error: 'Invalid response format: no mainWords found',
				rawResponse: response.rawResponse,
			};
		}

		// Validate secondary words
		if (
			!generatedSecondaryWords ||
			!Array.isArray(generatedSecondaryWords) ||
			generatedSecondaryWords.length === 0
		) {
			return {
				success: false,
				error: 'Invalid response format: no secondaryWords found',
				rawResponse: response.rawResponse,
			};
		}

		// Validate main words structure
		for (const wordObj of generatedMainWords) {
			if (!wordObj.word || !wordObj.clue) {
				return {
					success: false,
					error: 'Invalid main word structure in response',
					rawResponse: response.rawResponse,
				};
			}
		}

		// Validate secondary words structure
		for (const wordObj of generatedSecondaryWords) {
			if (!wordObj.word || !wordObj.clue) {
				return {
					success: false,
					error: 'Invalid secondary word structure in response',
					rawResponse: response.rawResponse,
				};
			}
		}

		// Create game data
		const gameData = new CrosswordPuzzleGameData();

		// Extract title from response (if provided)
		if (response.json.title) {
			gameData.title = response.json.title;
		}

		// Add main words and their clues
		// Always add ALL words from the response (both new and existing words with hints)
		// The merge logic will handle replacement/merging based on clearPrevious flag
		generatedMainWords.forEach((obj: { word: string; clue: string }) => {
			const word = obj.word.toUpperCase();
			// Add the word to mainWords array only if not already present
			if (!gameData.mainWords.includes(word)) {
				gameData.mainWords.push(word);
				// Add the clue
				gameData.mainWordClues[word] = obj.clue;
			} else {
				console.warn(
					`[Crossword Generation] Duplicate main word detected and skipped: "${word}"`,
				);
			}
		});

		// Add secondary words and their clues
		// Always add ALL words from the response (both new and existing words with hints)
		// The merge logic will handle replacement/merging based on clearPrevious flag
		generatedSecondaryWords.forEach(
			(obj: { word: string; clue: string }) => {
				const word = obj.word.toUpperCase();
				// Add the word to secondaryWords array only if not already present
				// Also check against mainWords to prevent same word appearing in both arrays
				if (
					!gameData.secondaryWords.includes(word) &&
					!gameData.mainWords.includes(word)
				) {
					gameData.secondaryWords.push(word);
					// Add the clue
					gameData.secondaryWordClues[word] = obj.clue;
				} else if (gameData.mainWords.includes(word)) {
					console.warn(
						`[Crossword Generation] Word "${word}" already exists as main word, skipping as secondary word`,
					);
				} else {
					console.warn(
						`[Crossword Generation] Duplicate secondary word detected and skipped: "${word}"`,
					);
				}
			},
		);

		return {
			success: true,
			gameData,
			rawResponse: response.rawResponse,
		};
	}

	/**
	 * Process optimized crossword response
	 * First processes words like mainAndSecondaryWords, then runs optimization
	 * Returns multiple optimization results or single best result
	 */
	private processOptimizedCrosswordResponse(
		response: any,
		parameters: any,
	): GenerationResult {
		// First, process the words using the same logic as mainAndSecondaryWords
		const wordProcessingResult = this.processMainAndSecondaryWordsResponse(
			response,
			parameters,
		);

		if (!wordProcessingResult.success || !wordProcessingResult.gameData) {
			return wordProcessingResult;
		}

		// Cast to CrosswordPuzzleGameData since we know the type
		const baseGameData =
			wordProcessingResult.gameData as CrosswordPuzzleGameData;

		// Extract optimization settings from parameters with defaults
		const defaults = this.getDefaultParameters();
		const settings: OptimizationSettings = {
			intensity:
				(parameters[
					'optimizationIntensity'
				] as OptimizationIntensity) || defaults.optimizationIntensity,
			maxGridRows: parameters['maxGridRows'] || defaults.maxGridRows,
			maxGridCols: parameters['maxGridCols'] || defaults.maxGridCols,
			optimizationResultCount:
				parameters['optimizationResultCount'] ||
				defaults.optimizationResultCount,
			sortMode:
				(parameters['sortMode'] as OptimizationSortMode) ||
				defaults.sortMode,
			testAllMainWordCombinations:
				parameters['testAllMainWordCombinations'] ??
				defaults.testAllMainWordCombinations,
			prioritizeShorterWords:
				parameters['prioritizeShorterWords'] ??
				defaults.prioritizeShorterWords,
		};

		console.log(
			'[Optimization] Running optimization with settings:',
			settings,
		);

		// Create a gameService instance for optimization within an injection context
		// This allows inject() calls in BaseGameService to work properly
		const gameService = runInInjectionContext(
			this.injector,
			() => new CrosswordPuzzleGameService(),
		);

		// Run optimization using the created gameService
		const optimizationResults = this.runOptimization(
			baseGameData,
			settings,
			gameService,
		);

		if (optimizationResults.length === 0) {
			return {
				success: false,
				error: 'Optimization produced no valid results',
				rawResponse: response.rawResponse,
			};
		}

		// If requesting single result (background mode), return the best one
		if (settings.optimizationResultCount === 1) {
			const bestResult = optimizationResults[0];
			const gameData = new CrosswordPuzzleGameData();

			// Copy base data from word generation
			Object.assign(gameData, baseGameData);

			// Apply optimized secondary words
			gameData.secondaryWords = [...bestResult.secondaryWords];

			// Include pre-generated grid data if available
			if (
				bestResult.grid &&
				bestResult.placedWords &&
				bestResult.intersections
			) {
				gameData.grid = bestResult.grid.map((row) =>
					row.map((cell) => ({ ...cell })),
				);
				gameData.placedWords = bestResult.placedWords.map((word) => ({
					...word,
				}));
				gameData.intersections = bestResult.intersections.map(
					(intersection) => ({
						...intersection,
					}),
				);
				gameData.gridRows = bestResult.gridRows;
				gameData.gridCols = bestResult.gridCols;

				gameData.highlightMainWord =
					parameters?.hightlightMainWord || false;
			}

			console.log(
				`[Optimization] Returning single best result (fill ratio: ${(bestResult.fillRatio * 100).toFixed(1)}%)`,
			);
			console.log('[Optimization] 📏 Final gameData grid dimensions:', {
				gridRows: gameData.gridRows,
				gridCols: gameData.gridCols,
				hasGrid: !!gameData.grid,
				gridLength: gameData.grid?.length,
				hasPlacedWords: !!gameData.placedWords,
				placedWordsCount: gameData.placedWords?.length,
			});

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		}

		// If requesting multiple results (modal mode), return array of results
		// Store results in a special format that the component can use
		console.log(
			`[Optimization] Returning ${optimizationResults.length} results for user selection`,
		);

		// Create a special result type that contains multiple optimization results
		// The component will handle displaying these as options
		// Note: This is a non-standard GameData that contains optimization results
		// The component will need to handle this specially
		const multiResultData = {
			optimizationResults: optimizationResults,
			baseGameData: baseGameData,
		};

		return {
			success: true,
			gameData: multiResultData as unknown as CrosswordPuzzleGameData,
			rawResponse: response.rawResponse,
		};
	}

	/**
	 * Validate crossword puzzle specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const generationType =
			request.parameters['generationType'] || 'mainSentence';

		if (generationType === 'mainSentence') {
			// Main words generation requires source material
			const hasSourceMaterial =
				request.sourceMaterial &&
				request.sourceMaterial.fileHandles.length > 0;

			if (!hasSourceMaterial) {
				return {
					valid: false,
					error: 'Valitse vähintään yksi artikkeli lähteeksi pääsanojen generointiin',
				};
			}
		} else if (generationType === 'secondaryWords') {
			// Secondary words generation requires main words
			const mainWords = request.parameters['mainWords'];
			if (
				!mainWords ||
				!Array.isArray(mainWords) ||
				mainWords.length === 0
			) {
				return {
					valid: false,
					error: 'Syötä ensin päälause ja jaa se sanoiksi ennen risteävien sanojen generointia',
				};
			}
		} else if (generationType === 'mainAndSecondaryWords') {
			// Combined generation requires source material
			const hasSourceMaterial =
				request.sourceMaterial &&
				request.sourceMaterial.fileHandles.length > 0;

			if (!hasSourceMaterial) {
				return {
					valid: false,
					error: 'Valitse vähintään yksi artikkeli lähteeksi kokonaisen ristikon generointiin',
				};
			}
		} else if (generationType === 'optimizedCrossword') {
			// Optimized crossword requires source material (like mainAndSecondaryWords)
			const hasSourceMaterial =
				request.sourceMaterial &&
				request.sourceMaterial.fileHandles.length > 0;

			if (!hasSourceMaterial) {
				return {
					valid: false,
					error: 'Valitse vähintään yksi artikkeli lähteeksi optimoidun ristikon generointiin',
				};
			}

			// Validate optimization parameters if provided
			const intensity = request.parameters['optimizationIntensity'];
			if (
				intensity &&
				!['quick', 'normal', 'thorough', 'exhaustive'].includes(
					intensity,
				)
			) {
				return {
					valid: false,
					error: `Virheellinen optimointitaso: ${intensity}`,
				};
			}

			const sortMode = request.parameters['sortMode'];
			if (sortMode && !['fillRatio', 'gridSize'].includes(sortMode)) {
				return {
					valid: false,
					error: `Virheellinen lajittelutapa: ${sortMode}`,
				};
			}
		} else {
			return {
				valid: false,
				error: `Tuntematon generointityyppi: ${generationType}`,
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for crossword puzzle generation
	 */
	getDefaultParameters(): any {
		return {
			generationType: 'mainAndSecondaryWords',
			mainWordAmount: 5,
			secondaryWordAmount: 30,
			selectedInterfaceId: 0,
			// Optimization parameters
			optimizationResultCount: 4,
			sortMode: 'fillRatio' as OptimizationSortMode,
			optimizationIntensity: 'normal' as OptimizationIntensity,
			maxGridRows: 20,
			maxGridCols: 20,
			testAllMainWordCombinations: true,
			prioritizeShorterWords: true,
			hightlightMainWord: false,
		};
	}

	/**
	 * Get default parameters for crossword puzzle generation
	 */
	override getDefaultBackgroundGenerationParameters(): any {
		return {
			generationType: 'optimizedCrossword',
			mainWordAmount: 5,
			secondaryWordAmount: 30,
			selectedInterfaceId: 0,
			// Optimization parameters
			optimizationResultCount: 1,
			sortMode: 'fillRatio' as OptimizationSortMode,
			optimizationIntensity: 'normal' as OptimizationIntensity,
			maxGridRows: 20,
			maxGridCols: 20,
			testAllMainWordCombinations: true,
			prioritizeShorterWords: true,
			hightlightMainWord: false,
		};
	}

	/**
	 * Override background steps to use optimization for batch generation.
	 * Background generation uses optimized crossword generation with single best result.
	 */
	override getDefaultBackgroundSteps(): GenerationStep[] {
		return [
			{
				stepId: 'main',
				stepType: 'generate',
				parameters: this.getDefaultBackgroundGenerationParameters(),
			},
		];
	}

	/**
	 * Merge new content with existing game data based on parameters
	 * @param existingGameData Current game data
	 * @param newGameData Generated game data
	 * @param clearPrevious Whether to clear existing content
	 * @returns Updated game data
	 */
	override mergeContent(
		existingGameData: CrosswordPuzzleGameData,
		newGameData: CrosswordPuzzleGameData,
		clearPrevious: boolean,
	): CrosswordPuzzleGameData {
		if (clearPrevious) {
			// Replace existing data with new data
			existingGameData.mainWords = [...(newGameData.mainWords || [])];
			existingGameData.secondaryWords = [
				...(newGameData.secondaryWords || []),
			];
			existingGameData.mainWordClues = { ...newGameData.mainWordClues };
			existingGameData.secondaryWordClues = {
				...newGameData.secondaryWordClues,
			};
			existingGameData.mainWordImageClues = {
				...newGameData.mainWordImageClues,
			};
			existingGameData.secondaryWordImageClues = {
				...newGameData.secondaryWordImageClues,
			};
		} else {
			// Merge new data with existing data
			// Only add words that don't already exist to prevent duplicates
			if (newGameData.mainWords) {
				const existingMainWordsUpper = existingGameData.mainWords.map(
					(w) => w.toUpperCase(),
				);
				for (const word of newGameData.mainWords) {
					if (!existingMainWordsUpper.includes(word.toUpperCase())) {
						existingGameData.mainWords.push(word);
					}
				}
			}
			if (newGameData.secondaryWords) {
				const existingSecondaryWordsUpper =
					existingGameData.secondaryWords.map((w) => w.toUpperCase());
				const existingMainWordsUpper = existingGameData.mainWords.map(
					(w) => w.toUpperCase(),
				);
				for (const word of newGameData.secondaryWords) {
					// Prevent duplicates within secondaryWords AND across to mainWords
					if (
						!existingSecondaryWordsUpper.includes(
							word.toUpperCase(),
						) &&
						!existingMainWordsUpper.includes(word.toUpperCase())
					) {
						existingGameData.secondaryWords.push(word);
					} else if (
						existingMainWordsUpper.includes(word.toUpperCase())
					) {
						console.warn(
							`[Crossword Merge] Skipping secondary word "${word}" as it already exists in main words`,
						);
					}
				}
			}

			// Merge clues (Object.assign correctly overwrites existing clues and adds new ones)
			Object.assign(
				existingGameData.mainWordClues,
				newGameData.mainWordClues,
			);
			Object.assign(
				existingGameData.secondaryWordClues,
				newGameData.secondaryWordClues,
			);
			Object.assign(
				existingGameData.mainWordImageClues,
				newGameData.mainWordImageClues,
			);
			Object.assign(
				existingGameData.secondaryWordImageClues,
				newGameData.secondaryWordImageClues,
			);
		}

		return existingGameData;
	}

	// ============================================================================
	// OPTIMIZATION METHODS
	// ============================================================================

	/**
	 * Calculate binomial coefficient C(n, k)
	 * Used for deterministic subset selection in optimization
	 */
	private binomialCoefficient(n: number, k: number): number {
		if (k > n) return 0;
		if (k === 0 || k === n) return 1;

		let result = 1;
		for (let i = 0; i < k; i++) {
			result *= n - i;
			result /= i + 1;
		}
		return Math.floor(result);
	}

	/**
	 * Get the combination at a specific lexicographic index
	 * This ensures deterministic selection - same input always produces same output
	 */
	private getCombinationAtIndex(
		array: string[],
		k: number,
		index: number,
	): string[] {
		const n = array.length;
		const combination: string[] = [];
		let remaining = index;
		let start = 0;

		for (let i = 0; i < k; i++) {
			for (let j = start; j < n; j++) {
				const count = this.binomialCoefficient(n - j - 1, k - i - 1);
				if (remaining < count) {
					combination.push(array[j]);
					start = j + 1;
					break;
				}
				remaining -= count;
			}
		}

		return combination;
	}

	/**
	 * Generate deterministic subsets for testing
	 * Selects evenly-spaced combinations to ensure good coverage
	 */
	private selectDeterministicSubsets(
		sortedWords: string[],
		subsetSize: number,
		numberOfSubsets: number,
	): string[][] {
		const subsets: string[][] = [];
		const totalCombinations = this.binomialCoefficient(
			sortedWords.length,
			subsetSize,
		);

		if (totalCombinations === 0) {
			return [];
		}

		if (totalCombinations <= numberOfSubsets) {
			// If we have fewer combinations than requested subsets, use all combinations
			for (let i = 0; i < totalCombinations; i++) {
				subsets.push(
					this.getCombinationAtIndex(sortedWords, subsetSize, i),
				);
			}
		} else {
			// Select evenly-spaced combinations
			const step = Math.floor(totalCombinations / numberOfSubsets);
			for (let i = 0; i < numberOfSubsets; i++) {
				const combinationIndex = i * step;
				subsets.push(
					this.getCombinationAtIndex(
						sortedWords,
						subsetSize,
						combinationIndex,
					),
				);
			}
		}

		return subsets;
	}

	/**
	 * Get optimization parameters based on intensity level
	 * Determines how thoroughly to test word combinations
	 */
	private getOptimizationParameters(intensity: OptimizationIntensity): {
		minWords: number;
		maxWords: number;
		combinationsPerSize: number;
		stepSize: number;
	} {
		const presets = {
			quick: {
				minWords: 10,
				maxWords: 15,
				combinationsPerSize: 2,
				stepSize: 3,
			},
			normal: {
				minWords: 8,
				maxWords: 20,
				combinationsPerSize: 5,
				stepSize: 2,
			},
			thorough: {
				minWords: 5,
				maxWords: 25,
				combinationsPerSize: 10,
				stepSize: 1,
			},
			exhaustive: {
				minWords: 2,
				maxWords: 30,
				combinationsPerSize: 20,
				stepSize: 1,
			},
		};

		return presets[intensity];
	}

	/**
	 * Evaluate a single word combination by generating a crossword grid
	 * Returns metrics about the generated grid for comparison
	 */
	private evaluateCombination(
		mainWords: string[],
		secondaryWords: string[],
		gameData: CrosswordPuzzleGameData,
		gameService: CrosswordPuzzleGameService,
	): OptimizationResult | null {
		// Use the provided game service (with proper injection context)
		const tempService = gameService;

		// CRITICAL: Reset service state before each evaluation
		// This prevents state accumulation that causes progressively larger grids
		console.log(
			'[evaluateCombination] 🔄 Calling resetForOptimization() before evaluation',
		);
		tempService.resetForOptimization();
		console.log('[evaluateCombination] ✅ Reset complete, grid state:', {
			gridRows: tempService.gridRows,
			gridCols: tempService.gridCols,
			placedWords: tempService.placedWords.length,
		});

		const tempGameData = new CrosswordPuzzleGameData();

		// Set up game data for evaluation
		tempGameData.mainWords = mainWords;
		tempGameData.secondaryWords = secondaryWords;
		tempGameData.mainWordClues = gameData.mainWordClues || {};
		tempGameData.secondaryWordClues = gameData.secondaryWordClues || {};

		// Copy intersection mode and other settings to ensure accurate evaluation
		tempGameData.allowMainWordIntersections =
			gameData.allowMainWordIntersections;
		tempGameData.mainWordActiveStatus = gameData.mainWordActiveStatus || {};
		tempGameData.mainWordImageClues = gameData.mainWordImageClues || {};

		tempService.gameData = tempGameData;

		// Generate crossword
		tempService.generateCrossword();

		// Validate connectivity - reject disconnected grids
		const isConnected = tempService.validateConnectivity();
		if (!isConnected) {
			console.log(
				`[Optimization] ❌ Rejecting disconnected grid for combination:`,
				{
					mainWords: mainWords,
					secondaryWords: secondaryWords,
					placedWords: tempService.placedWords.length,
					intersections: tempService.intersections.length,
				},
			);
			return null; // Reject this combination
		}

		// Calculate metrics - AFTER trimming
		const gridRows = tempService.gridRows;
		const gridCols = tempService.gridCols;
		const totalCells = gridRows * gridCols;

		// Count filled cells
		let filledCells = 0;
		for (let r = 0; r < gridRows; r++) {
			for (let c = 0; c < gridCols; c++) {
				if (tempService.grid[r][c].filled) {
					filledCells++;
				}
			}
		}

		const fillRatio = totalCells > 0 ? filledCells / totalCells : 0;

		// DEBUG: Log final grid dimensions after generation and trimming
		console.log('[evaluateCombination] 📊 Final grid dimensions:', {
			gridRows,
			gridCols,
			totalCells,
			filledCells,
			fillRatio: (fillRatio * 100).toFixed(1) + '%',
			mainWords: mainWords.length,
			secondaryWords: secondaryWords.length,
		});

		// Count placed secondary words and intersections
		const placedSecondaryWords = tempService.placedWords.filter(
			(w) => !w.isMainWord,
		);
		const mainWordIntersections = tempService.intersections.filter(
			(i) => i.isMainWord,
		).length;

		// Deep copy grid data to preserve the optimized result
		// This prevents the grid from being regenerated in the renderer
		const gridCopy: CrosswordCell[][] = tempService.grid.map((row) =>
			row.map((cell) => ({ ...cell })),
		);
		const placedWordsCopy: PlacedWord[] = tempService.placedWords.map(
			(word) => ({ ...word }),
		);
		const intersectionsCopy: WordIntersection[] =
			tempService.intersections.map((intersection) => ({
				...intersection,
			}));

		return {
			secondaryWords: secondaryWords,
			placedCount: placedSecondaryWords.length,
			totalCells,
			filledCells,
			fillRatio: totalCells > 0 ? filledCells / totalCells : 0,
			gridRows,
			gridCols,
			totalIntersections: tempService.intersections.length,
			mainWordIntersections,
			// Store pre-generated grid data
			grid: gridCopy,
			placedWords: placedWordsCopy,
			intersections: intersectionsCopy,
		};
	}

	/**
	 * Get best oversized results using weighted composite scoring
	 * Balances fill ratio (quality) vs grid size (constraint violation)
	 */
	private getBestOversizedResults(
		oversizedResults: OptimizationResult[],
		count: number,
		maxGridRows: number,
		maxGridCols: number,
	): OptimizationResult[] {
		const targetSize = maxGridRows * maxGridCols;

		console.log(
			`\n  🔍 Scoring ${oversizedResults.length} oversized results...`,
		);
		console.log(
			`  Target size: ${targetSize} cells (${maxGridCols}×${maxGridRows})`,
		);

		// Score each result with weighted composite score
		const scored = oversizedResults.map((result, idx) => {
			const actualSize = result.gridRows * result.gridCols;
			const gridSizeOverage =
				((actualSize - targetSize) / targetSize) * 100;

			// Fill ratio (0-100) minus 2x grid size penalty
			const score = result.fillRatio * 100 - gridSizeOverage * 2;

			if (idx < 3) {
				// Log first 3 for debugging
				console.log(
					`    ${idx + 1}. Grid: ${result.gridCols}×${result.gridRows} (${actualSize} cells), ` +
						`Fill: ${(result.fillRatio * 100).toFixed(1)}%, ` +
						`Overage: +${gridSizeOverage.toFixed(0)}%, ` +
						`Score: ${score.toFixed(1)}`,
				);
			}

			return {
				result,
				score,
				actualSize,
				gridSizeOverage,
			};
		});

		// Sort by composite score descending
		console.log(`  🔄 Sorting by composite score (descending)...`);
		const sorted = scored.sort((a, b) => b.score - a.score);

		console.log(`  📊 Top ${count} oversized results after sorting:`);
		sorted.slice(0, count).forEach((item, i) => {
			console.log(
				`    ${i + 1}. Grid: ${item.result.gridCols}×${item.result.gridRows} (${item.actualSize} cells), ` +
					`Fill: ${(item.result.fillRatio * 100).toFixed(1)}%, ` +
					`Overage: +${item.gridSizeOverage.toFixed(0)}%, ` +
					`Score: ${item.score.toFixed(1)}`,
			);
		});

		// Return top N results
		return sorted.slice(0, count).map((item) => item.result);
	}

	/**
	 * Filter and sort optimization results based on grid size constraints and sort mode
	 */
	private filterAndRankResults(
		allResults: OptimizationResult[],
		settings: OptimizationSettings,
	): OptimizationResult[] {
		console.log(
			`\n[Optimizer] Filtering ${allResults.length} total results...`,
		);
		console.log(
			`[Optimizer] Grid size limits: ${settings.maxGridCols}×${settings.maxGridRows}`,
		);

		// Separate valid and oversized results
		const validResults = allResults.filter(
			(r) =>
				r.gridRows <= settings.maxGridRows &&
				r.gridCols <= settings.maxGridCols,
		);
		const oversizedResults = allResults.filter(
			(r) =>
				r.gridRows > settings.maxGridRows ||
				r.gridCols > settings.maxGridCols,
		);

		console.log(
			`  ✅ Valid results (within ${settings.maxGridCols}×${settings.maxGridRows}): ${validResults.length}`,
		);
		console.log(
			`  ❌ Oversized results (exceeds limits): ${oversizedResults.length}`,
		);

		// DEBUG: Show oversized results with their actual dimensions
		if (oversizedResults.length > 0) {
			console.log(
				`  📏 First 5 oversized results (SHOULD NOT BE SELECTED):`,
			);
			oversizedResults.slice(0, 5).forEach((r, i) => {
				console.log(
					`     ${i + 1}. Grid: ${r.gridCols}×${r.gridRows} ` +
						`(exceeds ${settings.maxGridCols}×${settings.maxGridRows}), ` +
						`Fill: ${(r.fillRatio * 100).toFixed(1)}%`,
				);
			});
		}

		// Debug: Show first few results BEFORE sorting
		if (validResults.length > 0) {
			console.log(`  📊 First 3 valid results BEFORE sorting:`);
			validResults.slice(0, 3).forEach((r, i) => {
				console.log(
					`     ${i + 1}. Grid: ${r.gridCols}×${r.gridRows}, Fill: ${(r.fillRatio * 100).toFixed(1)}%`,
				);
			});
		}

		// Sort valid results based on sort mode
		let sortedValidResults: OptimizationResult[];
		if (settings.sortMode === 'gridSize') {
			// Sort by grid size ascending (smallest first)
			sortedValidResults = validResults.sort((a, b) => {
				const sizeA = a.gridRows * a.gridCols;
				const sizeB = b.gridRows * b.gridCols;
				if (sizeA !== sizeB) {
					return sizeA - sizeB;
				}
				// Tie-breaker: fill ratio descending
				return b.fillRatio - a.fillRatio;
			});
			console.log(`  🔄 Sorted by: grid size ascending (smallest first)`);
		} else {
			// Sort by fill ratio descending (densest first)
			sortedValidResults = validResults.sort(
				(a, b) => b.fillRatio - a.fillRatio,
			);
			console.log(
				`  🔄 Sorted by: fill ratio descending (densest first)`,
			);
		}

		// Debug: Show first few results AFTER sorting
		if (sortedValidResults.length > 0) {
			console.log(`  📊 First 3 valid results AFTER sorting:`);
			sortedValidResults.slice(0, 3).forEach((r, i) => {
				console.log(
					`     ${i + 1}. Grid: ${r.gridCols}×${r.gridRows}, Fill: ${(r.fillRatio * 100).toFixed(1)}%`,
				);
			});
		}

		// Fill up to optimizationResultCount with best oversized if needed
		if (
			sortedValidResults.length < settings.optimizationResultCount &&
			oversizedResults.length > 0
		) {
			const needed =
				settings.optimizationResultCount - sortedValidResults.length;
			console.log(
				`\n⚠️  Only ${sortedValidResults.length} valid result(s) found within ${settings.maxGridCols}×${settings.maxGridRows}`,
			);
			console.log(
				`📦 Adding ${needed} best oversized result(s) as fallback`,
			);

			const bestOversized = this.getBestOversizedResults(
				oversizedResults,
				needed,
				settings.maxGridRows,
				settings.maxGridCols,
			);

			// Combine valid results with fallback oversized results
			const combined = [...sortedValidResults, ...bestOversized];
			console.log(`\n📋 Final ${combined.length} results to return:`);
			combined.forEach((r, i) => {
				console.log(
					`   ${i + 1}. Grid: ${r.gridCols}×${r.gridRows}, Fill: ${(r.fillRatio * 100).toFixed(1)}%`,
				);
			});
			return combined;
		}

		// Return top N valid results
		const finalResults = sortedValidResults.slice(
			0,
			settings.optimizationResultCount,
		);
		console.log(`\n📋 Final ${finalResults.length} results to return:`);
		finalResults.forEach((r, i) => {
			console.log(
				`   ${i + 1}. Grid: ${r.gridCols}×${r.gridRows}, Fill: ${(r.fillRatio * 100).toFixed(1)}%`,
			);
		});
		return finalResults;
	}

	/**
	 * Generate word subsets for testing based on optimization settings
	 * Returns array of word combinations to evaluate
	 */
	private generateWordSubsets(
		secondaryWords: string[],
		intensity: OptimizationIntensity,
	): string[][] {
		// Sort words for deterministic results
		const sortedSecondaryWords = [...secondaryWords].sort();

		const params = this.getOptimizationParameters(intensity);

		// Strategy: Test different subset sizes and combinations
		const minWords = Math.min(params.minWords, sortedSecondaryWords.length);
		const maxWords = Math.min(params.maxWords, sortedSecondaryWords.length);
		const testSizes = [];

		for (let size = minWords; size <= maxWords; size += params.stepSize) {
			testSizes.push(size);
		}

		// For each subset size, generate multiple deterministic combinations
		const allSubsets: string[][] = [];
		for (const subsetSize of testSizes) {
			const subsets = this.selectDeterministicSubsets(
				sortedSecondaryWords,
				subsetSize,
				params.combinationsPerSize,
			);
			allSubsets.push(...subsets);
		}

		return allSubsets;
	}

	/**
	 * Run the optimization algorithm and return top N results
	 * This is the main optimization orchestrator method
	 */
	private runOptimization(
		gameData: CrosswordPuzzleGameData,
		settings: OptimizationSettings,
		gameService: CrosswordPuzzleGameService,
	): OptimizationResult[] {
		console.log('\n[Crossword Optimizer] Starting optimization');
		console.log(`  Intensity: ${settings.intensity}`);
		console.log(`  Sort mode: ${settings.sortMode}`);
		console.log(`  Result count: ${settings.optimizationResultCount}`);
		console.log(
			`  Max grid size: ${settings.maxGridCols}×${settings.maxGridRows}`,
		);

		// Validate inputs
		if (!gameData.mainWords || gameData.mainWords.length === 0) {
			console.warn('[Optimizer] No main words to optimize');
			return [];
		}

		if (!gameData.secondaryWords || gameData.secondaryWords.length === 0) {
			console.warn('[Optimizer] No secondary words to optimize');
			return [];
		}

		// Generate word subsets to test
		const wordSubsets = this.generateWordSubsets(
			gameData.secondaryWords,
			settings.intensity,
		);

		console.log(`  Testing ${wordSubsets.length} word combinations`);

		// Filter main words by active status (only use words that are not explicitly marked as inactive)
		const activeMainWords = gameData.mainWords.filter(
			(word) => gameData.mainWordActiveStatus?.[word] !== false,
		);
		console.log(
			`  Using ${activeMainWords.length} active main words (${gameData.mainWords.length - activeMainWords.length} inactive)`,
		);

		// Evaluate each subset
		const allResults: OptimizationResult[] = [];
		for (let i = 0; i < wordSubsets.length; i++) {
			const subset = wordSubsets[i];
			const result = this.evaluateCombination(
				activeMainWords,
				subset,
				gameData,
				gameService,
			);
			if (result !== null) {
				allResults.push(result);
			}
		}

		console.log(
			`[Optimization] Evaluated ${wordSubsets.length} combinations, ${allResults.length} valid (connected)`,
		);

		if (allResults.length === 0) {
			throw new Error(
				'No valid connected crossword grids found. All combinations resulted in disconnected word groups.',
			);
		}

		// Filter and rank results
		const topResults = this.filterAndRankResults(allResults, settings);

		console.log('\n[Crossword Optimizer] ⭐ OPTIMIZATION COMPLETE ⭐');
		console.log(`  Found ${topResults.length} result(s)`);
		if (topResults.length > 0) {
			console.log('\n  📋 Top Results:');
			topResults.forEach((result, index) => {
				console.log(
					`     ${index + 1}. Grid: ${result.gridCols}×${result.gridRows}, ` +
						`Fill: ${(result.fillRatio * 100).toFixed(1)}%, ` +
						`Words: ${result.placedCount}/${result.secondaryWords.length}`,
				);
			});
		}

		return topResults;
	}
}
